import React, { useMemo } from "react"
import { cva } from "class-variance-authority"
import cn from "../../../utils/classes"

export type TextColors =
  | "blue"
  | "black"
  | "gray"
  | "mediumGray"
  | "red"
  | "lightBlue"
  | "purple"
  | "lightPurple"
  | "orange"
export type FontWeights = "normal" | "medium" | "bold"
export type FontSizes =
  | "small"
  | "sm"
  | "medium"
  | "md"
  | "large"
  | "lg"
  | "xl"
  | "xxl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl"
  | "7xl"

export interface LabelProps {
  /** `text` is optional if you want to pass in children instead */
  text?: string
  isRequired?: boolean
  isTitle?: boolean
  fontWeight?: FontWeights
  size?: FontSizes
  color?: TextColors
  wrap?: boolean
  truncate?: boolean
  fontType?: string
  underlined?: boolean
  textCentered?: boolean
  styleOverrides?: string
  requiredAsteriskStyleOverrides?: string
  htmlFor: string
  onClick?: React.MouseEventHandler<HTMLLabelElement>
}

/**
 * @param {string} text - The text of the label.
 * @param {string} htmlFor - This is an accessibility property, it describes the element labelled.
 */
export const Label: React.FC<React.PropsWithChildren<LabelProps>> = ({
  text,
  isRequired = false,
  isTitle = false,
  fontWeight = "normal",
  size = "medium",
  color = "gray",
  wrap = false,
  truncate = false,
  fontType = "",
  underlined = false,
  textCentered = false,
  styleOverrides,
  requiredAsteriskStyleOverrides,
  children,
  htmlFor,
  onClick,
}) => {
  const variants = cva([], {
    // TODO: Dedupe the size list
    variants: {
      size: {
        sm: "text-sm",
        small: "text-sm",
        md: "text-base",
        medium: "text-base",
        lg: "text-lg",
        large: "text-lg",
        xl: "text-xl",
        xxl: "text-2xl",
        "3xl": "text-3xl",
        "4xl": "text-4xl",
        "5xl": "text-5xl",
        "6xl": "text-6xl",
        "7xl": "text-7xl",
      },
      isTitle: {
        true: "font-bold text-title",
      },
      fontWeight: {
        bold: "font-bold",
        medium: "font-medium",
        normal: "",
      },
      color: {
        black: "text-black",
        gray: "text-gray-900",
        mediumGray: "text-gray-500",
        blue: "text-font-blue",
        purple: "text-purple-brand",
        lightBlue: "text-link-blue",
        lightPurple: "text-light-purple-brand",
        red: "text-red-700",
        orange: "text-orange-800",
      },
      wrap: {
        false: "whitespace-nowrap",
      },
      truncate: {
        true: "truncate",
      },
      underlined: {
        true: "underline",
      },
      textCentered: {
        true: "text-center",
      },
    },
  })

  const className = useMemo(() => {
    return cn(
      variants({
        size,
        isTitle,
        fontWeight,
        color,
        wrap,
        truncate,
        underlined,
        textCentered,
      }),
      fontType,
      styleOverrides,
    )
  }, [
    size,
    isTitle,
    fontWeight,
    color,
    wrap,
    truncate,
    underlined,
    textCentered,
    fontType,
    styleOverrides,
    variants,
  ])

  return (
    <label className={className} htmlFor={htmlFor} onClick={onClick}>
      {isRequired && (
        <span
          className={cn("text-red-600", requiredAsteriskStyleOverrides)}
          aria-hidden="true"
        >
          *
        </span>
      )}
      {text}
      {children}
    </label>
  )
}

export default Label
